import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Skeleton} from '@/components/ui/skeleton'
import {PlanGate} from '@/components/plan-upsell'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useRegenerateRegistrationLink, useRegistrationLink} from '@/hooks/sgsst/riesgos/use-entrevista'
import {isBusinessProfileIncomplete, translateApiError} from '@/lib/errors'

/** The 409 «business profile incomplete»: the questionnaire branches on CIIU, clase de riesgo
 * and ciudad, so they come first. */
function BlockedState({onEditProfile}: {onEditProfile?: () => void}) {
  const {t} = useTranslation()
  return (
    <div className="notice-amber space-y-2 rounded-lg px-3.5 py-3 text-xs" data-testid="registration-link-blocked">
      <p>{t('business.registrationLink.blockedBody')}</p>
      {onEditProfile && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onEditProfile}
          data-testid="registration-link-blocked-edit">
          {t('business.registrationLink.blockedCta')}
        </Button>
      )}
    </div>
  )
}

/** Regenerating revokes the live token at once: the old link reads as an unknown token. */
export function RegistrationLinkPanel({
  businessId,
  canEdit,
  onEditProfile,
}: {
  businessId: string
  canEdit: boolean
  onEditProfile?: () => void
}) {
  const {t} = useTranslation()
  const {data: link, error: linkError} = useRegistrationLink(businessId)
  const regenerate = useRegenerateRegistrationLink(businessId)
  const {data: business} = useBusiness(businessId)
  const {can_manage_employees} = useCapabilities(businessId)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [regenerateError, setRegenerateError] = useState<string | null>(null)
  const [regenerateBlocked, setRegenerateBlocked] = useState(false)

  if (!link) {
    if (isBusinessProfileIncomplete(linkError)) return <BlockedState onEditProfile={onEditProfile} />
    return <Skeleton className="h-16 w-full" data-testid="registration-link-loading" />
  }

  const href = `${window.location.origin}/registro/${link.token}`

  async function copy() {
    try {
      await navigator.clipboard.writeText(href)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  function regenerateLink() {
    setRegenerateError(null)
    regenerate.mutate(undefined, {
      onSuccess: () => setConfirmOpen(false),
      onError: (err) => {
        setConfirmOpen(false)
        if (isBusinessProfileIncomplete(err)) setRegenerateBlocked(true)
        else setRegenerateError(translateApiError(err))
      },
    })
  }

  if (regenerateBlocked) return <BlockedState onEditProfile={onEditProfile} />

  return (
    <>
      <div className="flex items-center gap-2">
        <a
          href={`/registro/${link.token}`}
          data-testid="registration-link-href"
          className="min-w-0 flex-1 truncate rounded-lg bg-secondary/60 px-2.5 py-1.5 font-mono text-xs text-foreground hover:underline">
          {href}
        </a>
        <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="registration-link-copy">
          {copied ? t('business.registrationLink.copied') : t('business.registrationLink.copy')}
        </Button>
        {canEdit && (
          <PlanGate
            locked={!can_manage_employees}
            feature={t('business.registrationLink.invalidate')}
            businessId={businessId}
            businessName={business?.name}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="registration-link-regenerate"
              onClick={() => setConfirmOpen(true)}>
              {t('business.registrationLink.invalidate')}
            </Button>
          </PlanGate>
        )}
      </div>
      <div className="notice-amber rounded-lg px-3.5 py-3 text-xs" data-testid="registration-link-existing-notice">
        {t('business.registrationLink.existingEmployeeNotice')}
      </div>
      {regenerateError && (
        <p className="text-xs text-destructive" data-testid="registration-link-regenerate-error">
          {regenerateError}
        </p>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('business.registrationLink.regenerateConfirmTitle')}
        description={t('business.registrationLink.regenerateConfirmBody')}
        porque={{
          label: t('business.registrationLink.regeneratePorqueLabel'),
          testId: 'registration-link-regenerate-porque',
          contenidoTestId: 'registration-link-regenerate-porque-contenido',
          contenido: <p>{t('business.registrationLink.regeneratePorqueBody')}</p>,
        }}
        confirmLabel={t('business.registrationLink.regenerateConfirmAction')}
        cancelLabel={t('common.cancel')}
        pending={regenerate.isPending}
        onConfirm={regenerateLink}
      />
    </>
  )
}

/** Renders even with an incomplete profile: the popover then shows the blocked state. */
export function RegistrationLinkCard({
  businessId,
  canEdit,
  onEditProfile,
}: {
  businessId: string
  canEdit: boolean
  onEditProfile?: () => void
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const {data: link, error: linkError} = useRegistrationLink(businessId)

  if (!link && !isBusinessProfileIncomplete(linkError)) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline" data-testid="registration-link-trigger">
          {t('business.registrationLink.title')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2 p-4" data-testid="registration-link-popover">
        <p className="text-sm font-medium text-foreground">{t('business.registrationLink.title')}</p>
        <p className="text-xs text-muted-foreground">{t('business.registrationLink.body')}</p>
        <RegistrationLinkPanel
          businessId={businessId}
          canEdit={canEdit}
          onEditProfile={
            onEditProfile
              ? () => {
                  setOpen(false)
                  onEditProfile()
                }
              : undefined
          }
        />
        {link && (
          <p className="text-xs text-muted-foreground" data-testid="registration-link-next-step">
            {t('business.registrationLink.nextStep')}
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}
